import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import { useT } from '../i18n/index.js';
import { Badge, Banner, Card, Spinner } from '../components/ui.js';

/**
 * MIS DISEÑOS.
 *
 * Los borradores se guardaban solos desde el primer día y se conservan 90, pero
 * el cliente no tenía manera de volver a ellos: solo existía el enlace directo
 * que le quedara en el historial del navegador. Estaban ahí y no se veían.
 *
 * Se dice CUÁNDO desaparece cada uno. No es un detalle legal: es la diferencia
 * entre que el cliente vuelva a tiempo o encuentre el hueco donde estaba su
 * trabajo.
 */

interface Diseno {
  id: string;
  productName: string;
  colorName: string;
  zoneName: string;
  layers: number;
  updatedAt: string;
  deleteAt: string | null;
  thumbnailUrl: string | null;
}

/** Días que faltan para que se borre, o null si no caduca. */
function diasParaBorrado(deleteAt: string | null): number | null {
  if (!deleteAt) return null;
  const dias = Math.ceil((new Date(deleteAt).getTime() - Date.now()) / 86_400_000);
  return Number.isFinite(dias) ? dias : null;
}

export function MisDisenosPage() {
  const t = useT();
  const [items, setItems] = useState<Diseno[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    api
      .misDisenos()
      .then((r) => {
        if (vivo) setItems(r.items as Diseno[]);
      })
      .catch((err: Error) => {
        if (vivo) setError(err.message);
      });
    return () => {
      vivo = false;
    };
  }, []);

  if (error) {
    return (
      <div className="mx-auto max-w-3xl p-4">
        <Banner tone="error" title={t('app.error')}>{error}</Banner>
      </div>
    );
  }
  if (!items) return <Spinner label={t('app.loading')} />;

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <h1 className="text-2xl font-semibold">{t('designs.title')}</h1>

      {items.length === 0 ? (
        <Card className="mt-4">
          <p className="text-sm text-tinta-suave">{t('designs.empty')}</p>
          <Link to="/" className="toque mt-3 inline-block rounded-lg bg-marca px-4 py-2 text-sm text-white">
            {t('designs.browse')}
          </Link>
        </Card>
      ) : (
        <ul className="mt-4 space-y-2">
          {items.map((diseno) => {
            const dias = diasParaBorrado(diseno.deleteAt);
            return (
              <li key={diseno.id}>
                <Link
                  to={`/editor/${diseno.id}`}
                  data-test="mi-diseno"
                  className="toque flex items-center gap-3 rounded-lg border border-borde p-3"
                >
                  {diseno.thumbnailUrl ? (
                    <img
                      src={diseno.thumbnailUrl}
                      alt=""
                      className="h-14 w-14 shrink-0 rounded object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <span className="h-14 w-14 shrink-0 rounded bg-fondo-suave" aria-hidden="true" />
                  )}

                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium">{diseno.productName}</span>
                    <span className="block truncate text-xs text-tinta-suave">
                      {diseno.colorName}
                      {diseno.layers > 0 ? ` · ${t('designs.layers', { n: diseno.layers })}` : ''}
                    </span>
                  </span>

                  {/* El aviso solo cuando queda poco: enseñarlo siempre lo
                      convierte en ruido y deja de leerse justo cuando importa. */}
                  {dias !== null && dias <= 14 && (
                    <Badge tone={dias <= 3 ? 'error' : 'warning'}>{t('designs.expiresIn', { n: dias })}</Badge>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
